import React, { useState, useEffect, useMemo } from 'react';
import { Ticket } from '../types';
import { Button, Flex, Input, Text, Card } from './base-components';
import {
  useSearchCrowdVoltEvents,
  useCrowdVoltEventTicketTypes,
  useCreateDualListing,
  useCrowdVoltStatus,
} from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import { trackEvent } from '../services/analytics';

interface Props {
  ticket: Ticket | null;
  open: boolean;
  onClose: () => void;
  onConnectCV?: () => void;
}

interface CVEventOption {
  event_uqid: string;
  name: string;
  date?: string;
  venue?: string;
}

interface WalletCandidate {
  wallet_uqid: string;
  wallet_system?: string;
  phone?: string;
  email?: string;
  name?: string;
}

export const ListForSaleModal: React.FC<Props> = ({ ticket, open, onClose, onConnectCV }) => {
  const { showSuccess, showError } = useToast();
  const cvStatus = useCrowdVoltStatus();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<CVEventOption | null>(null);
  const [ticketTypeUqid, setTicketTypeUqid] = useState('');
  const [price, setPrice] = useState('');
  const [qty, setQty] = useState('1');
  const [walletCandidates, setWalletCandidates] = useState<WalletCandidate[] | null>(null);
  const [chosenWalletUqid, setChosenWalletUqid] = useState('');
  const [ignoreLowPrice, setIgnoreLowPrice] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const isCVConnected = !!cvStatus.data?.connected;

  // Debounce the search query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Default search query to the ticket's event name when modal opens
  useEffect(() => {
    if (open && ticket && !query) {
      const ev = typeof ticket.event === 'object' ? (ticket.event as any) : null;
      const name = ev?.name || ticket.event_name || '';
      if (name) setQuery(name);
    }
  }, [open, ticket, query]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
      setSelectedEvent(null);
      setTicketTypeUqid('');
      setPrice('');
      setQty('1');
      setWalletCandidates(null);
      setChosenWalletUqid('');
      setIgnoreLowPrice(false);
      setLastError(null);
    }
  }, [open]);

  const searchQ = useSearchCrowdVoltEvents(debouncedQuery, { enabled: isCVConnected && open });
  const ttQ = useCrowdVoltEventTicketTypes(selectedEvent?.event_uqid || null);
  const createMut = useCreateDualListing();

  const selectedTicketType = useMemo(
    () => (ttQ.data?.ticket_types || []).find((t) => t.ticket_type_uqid === ticketTypeUqid) || null,
    [ttQ.data, ticketTypeUqid],
  );

  if (!open) return null;

  if (!isCVConnected) {
    // Gentle nudge to connect first
    return (
      <Flex align="center" justify="center" fixed inset bg="black/80" backdrop zIndex={1000} animate="fadeIn" onClick={onClose}>
        <Card fullWidth maxWidth="sm" shadow animate="slideUp" bg="dark" padding="lg" hover={false} onClick={(e) => e.stopPropagation()}>
          <Text variant="heading" mb={2}>Connect CrowdVolt first</Text>
          <Text variant="muted" size="sm" mb={4}>
            Cross‑listing to CrowdVolt requires a connected account. Connect, then come back to list this ticket.
          </Text>
          <Flex justify="end" gap={2}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={() => { onClose(); onConnectCV?.(); }}>Connect CrowdVolt</Button>
          </Flex>
        </Card>
      </Flex>
    );
  }

  const handleSubmit = async () => {
    if (!ticket) return;
    setLastError(null);

    if (!selectedEvent) { showError('Pick a CrowdVolt event'); return; }
    if (!ticketTypeUqid || !selectedTicketType) { showError('Pick a ticket type'); return; }
    const parsedPrice = parseFloat(price);
    const parsedQty = parseInt(qty, 10);
    if (!parsedPrice || parsedPrice <= 0) { showError('Price must be > 0'); return; }
    if (!parsedQty || parsedQty <= 0) { showError('Quantity must be > 0'); return; }
    if (walletCandidates && !chosenWalletUqid) { showError('Pick a CrowdVolt wallet'); return; }

    try {
      const res = await createMut.mutateAsync({
        ticket_id: ticket.id,
        event_uqid: selectedEvent.event_uqid,
        ticket_type_uqid: selectedTicketType.ticket_type_uqid,
        ticket_type_name: selectedTicketType.name,
        price: parsedPrice,
        qty: parsedQty,
        preferred_wallet_uqid: chosenWalletUqid || undefined,
        ignore_low_price: ignoreLowPrice,
      });
      showSuccess(`Listed for $${parsedPrice.toFixed(2)} on FlashTickets + CrowdVolt`);
      trackEvent('dual_listing_created', {
        ftListingId: res.ft_listing_id,
        cvAskUqid: res.cv_ask_uqid,
        price: parsedPrice,
        qty: parsedQty,
      });
      onClose();
    } catch (err: any) {
      // Wallet ambiguity: backend returned 400 with candidates → render dropdown
      if (err?.status === 400 && Array.isArray(err?.body?.candidates) && err.body.candidates.length > 0) {
        setWalletCandidates(err.body.candidates);
        setLastError('Pick which CrowdVolt wallet to list under');
        return;
      }
      // Price-floor warning: backend returned 400 with is_too_low → offer override
      if (err?.status === 400 && err?.body?.is_too_low) {
        setLastError('Your price is below CrowdVolt’s floor. Check "Allow below floor" to override.');
        return;
      }
      setLastError(err?.message || 'Failed to create listing');
    }
  };

  const events = searchQ.data?.events || [];
  const ticketTypes = ttQ.data?.ticket_types || [];

  return (
    <Flex align="center" justify="center" fixed inset bg="black/80" backdrop zIndex={1000} animate="fadeIn" onClick={onClose}>
      <Card fullWidth maxWidth="md" shadow animate="slideUp" bg="dark" padding="lg" hover={false} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Flex align="center" gap={3} mb={4}>
          <div className="w-9 h-9 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
            <span className="text-violet-400 text-sm font-bold">⚡</span>
          </div>
          <div>
            <Text variant="heading">List for sale</Text>
            <Text variant="muted" size="sm">
              Posts to FlashTickets and CrowdVolt at the same time
            </Text>
          </div>
        </Flex>

        {/* CV event search */}
        <div className="mb-3">
          <Input
            label="CrowdVolt event"
            placeholder="Search by artist or event name…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedEvent(null); setTicketTypeUqid(''); }}
          />
          {debouncedQuery.length >= 2 && !selectedEvent && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-white/[0.06] bg-white/[0.02]">
              {searchQ.isLoading && <div className="px-3 py-2 text-sm text-white/40">Searching…</div>}
              {!searchQ.isLoading && events.length === 0 && (
                <div className="px-3 py-2 text-sm text-white/40">No matches</div>
              )}
              {events.map((ev) => (
                <button
                  key={ev.event_uqid}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-white/[0.04] border-b border-white/[0.04] last:border-b-0"
                  onClick={() => { setSelectedEvent(ev); setTicketTypeUqid(''); }}
                >
                  <div className="text-sm text-white/90">{ev.name}</div>
                  <div className="text-xs text-white/40">
                    {[ev.venue, ev.date].filter(Boolean).join(' · ')}
                  </div>
                </button>
              ))}
            </div>
          )}
          {selectedEvent && (
            <div className="mt-2 flex items-center justify-between p-2 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <div>
                <div className="text-sm text-violet-300">{selectedEvent.name}</div>
                <div className="text-xs text-white/40">{[selectedEvent.venue, selectedEvent.date].filter(Boolean).join(' · ')}</div>
              </div>
              <button type="button" className="text-xs text-white/50 hover:text-white/80" onClick={() => { setSelectedEvent(null); setTicketTypeUqid(''); }}>
                change
              </button>
            </div>
          )}
        </div>

        {/* Ticket type picker */}
        {selectedEvent && (
          <div className="mb-3">
            <label className="block text-sm text-white/70 mb-1">Ticket type</label>
            {ttQ.isLoading ? (
              <div className="text-sm text-white/40 px-2 py-1.5">Loading ticket types…</div>
            ) : (
              <select
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white/90"
                value={ticketTypeUqid}
                onChange={(e) => setTicketTypeUqid(e.target.value)}
              >
                <option value="">Select a ticket type…</option>
                {ticketTypes.map((tt) => (
                  <option key={tt.ticket_type_uqid} value={tt.ticket_type_uqid}>
                    {tt.name}{tt.price_floor ? ` (floor $${tt.price_floor})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Price + qty */}
        <Flex gap={3} mb={3}>
          <Flex direction="col" grow>
            <Input
              type="number"
              label="Price ($)"
              placeholder="0.00"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </Flex>
          <Flex direction="col" grow>
            <Input
              type="number"
              label="Qty"
              placeholder="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </Flex>
        </Flex>

        {/* Wallet candidates (only shown when backend signaled ambiguity) */}
        {walletCandidates && walletCandidates.length > 0 && (
          <div className="mb-3">
            <label className="block text-sm text-white/70 mb-1">CrowdVolt wallet</label>
            <select
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white/90"
              value={chosenWalletUqid}
              onChange={(e) => setChosenWalletUqid(e.target.value)}
            >
              <option value="">Select a wallet…</option>
              {walletCandidates.map((w) => (
                <option key={w.wallet_uqid} value={w.wallet_uqid}>
                  {[w.name, w.phone, w.email].filter(Boolean).join(' · ') || w.wallet_uqid}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Low-price override */}
        <label className="flex items-center gap-2 text-sm text-white/60 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={ignoreLowPrice}
            onChange={(e) => setIgnoreLowPrice(e.target.checked)}
          />
          Allow below CrowdVolt floor
        </label>

        {/* Error */}
        {lastError && (
          <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-200">
            {lastError}
          </div>
        )}

        {/* CV-first note */}
        <div className="mb-4 p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
          <p className="text-xs text-white/50">
            CrowdVolt is posted first. If CrowdVolt fails, no FlashTickets listing is created.
          </p>
        </div>

        <Flex justify="end" gap={2}>
          <Button variant="secondary" onClick={onClose} disabled={createMut.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={createMut.isPending}>
            {createMut.isPending ? 'Listing…' : 'List for sale'}
          </Button>
        </Flex>
      </Card>
    </Flex>
  );
};
